export const BASE_URL = (import.meta.env?.VITE_API_URL as string | undefined) ?? 'http://localhost:8787';

interface FetchOptions {
  method?: string;
  body?: unknown;
  skipAuth?: boolean;
  authToken?: string;
  keepalive?: boolean;
  skipUnauthorizedHandler?: boolean;
  signal?: AbortSignal;
  responseType?: 'json' | 'text';
}

/**
 * Typed fetch wrapper for the AstdZ REST API. Injects a Bearer token from
 * sessionStorage unless `skipAuth` is set, and emits `astdz:unauthorized` on 401
 * when a token was actually sent so the app can force-logout.
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (!options.skipAuth) {
    try {
      const sessionToken = sessionStorage.getItem('astdz_session_token');
      if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;
    } catch {
      // ignore
    }
  }

  if (options.authToken) headers['Authorization'] = `Bearer ${options.authToken}`;

  const init: RequestInit = {
    method: options.method ?? (options.body ? 'POST' : 'GET'),
    headers,
    keepalive: options.keepalive ?? false,
    signal: options.signal ?? null,
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  const res = await fetch(`${BASE_URL}${path}`, init);

  // Only force-logout when we actually tried to authenticate.
  // Public endpoints (no auth header) returning 401 should NOT log the user out.
  if (res.status === 401 && headers['Authorization'] && !options.skipUnauthorizedHandler
    && headers['Authorization'] === `Bearer ${sessionStorage.getItem('astdz_session_token')}`) {
    window.dispatchEvent(new Event('astdz:unauthorized'));
  }

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const err = (await res.json()) as { error?: string; message?: string };
      message = err.error ?? err.message ?? message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (options.responseType === 'text') return text as T;
  return text ? JSON.parse(text) as T : undefined as T;
}

/** GET `path` on the AstdZ API with auth. */
export const apiGet = <T>(path: string) => apiFetch<T>(path);
/** POST `body` as JSON to `path` on the AstdZ API with auth. */
export const apiPost = <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body });
